import { AdminContractItemDto, ContractSummaryDto } from 'api/contract';
import {
  Eye,
  Shield,
  Users,
  Calendar,
  DollarSign,
  Building2,
  Network,
  Package,
  Euro,
  Currency
} from 'lucide-react';
import { contractsAPI } from 'services/swagger-api';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import {
  FacetQueryCommand,
  mapFacetFiltersToFiltersDto
} from 'shared/contexts/table-context/facetFilters';
import { buildExportCommand, exportListCsv } from 'shared/contexts/table-context/exportCsv';
import { OnCreateConfig } from 'shared/contexts/table-context/components/types';

/**
 * Item des deux listes de contrats : le back sérialise le même ReadContratResultDto,
 * la liste admin l'enrichit des dimensions plateforme (spec OpenAPI : les deux
 * schémas étendent BaseContract). Assignabilité structurelle vérifiée par TS —
 * toute divergence future des DTOs générés casse ici à la compilation.
 */
export type ContractListItem = ContractSummaryDto &
  Partial<Pick<AdminContractItemDto, 'plateformeId' | 'plateformeName'>>;

interface GenericContractsTableProps {
  forcedFilters?: Record<string, any>;
  title?: string;
  onCreateConfig?: OnCreateConfig;
  selectable?: boolean;
  onViewClick: (contract: ContractListItem) => void;
  enableFilters?: boolean;
  pageSize?: number;
  /**
   * Périmètre courtier : liste/facettes via les endpoints périmétrés
   * (/contrats/liste) au lieu des endpoints admin (403 hors rôle admin).
   * L'export CSV (route admin) et la colonne Plateforme sont désactivés.
   */
  brokerScope?: boolean;
}

export function GenericContractsTable({
  forcedFilters = {},
  title,
  onCreateConfig,
  onViewClick,
  selectable = true,
  enableFilters = true,
  pageSize = 10,
  brokerScope = false,
}: GenericContractsTableProps) {

  // Actions par défaut
  const handleView = (contract: ContractListItem) => {
    if (onViewClick)
      onViewClick(contract);
  };

  // Liste admin (toutes plateformes) — remplace l'ancienne route liste-global —
  // ou liste périmétrée courtier selon brokerScope (mêmes items ReadContratResultDto côté back).
  // Les filtres du panneau à facettes (indexés par facet.key) sont mappés vers le
  // FiltersDto ; les forcedFilters (déjà au format FiltersDto) restent prioritaires.
  const fetchContracts = async (params: any) => {
    const command = {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      searchString: params.searchString || '',
      // Tri serveur par colonne (omis -> tri par défaut du back)
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
      filters: {
        ...mapFacetFiltersToFiltersDto(params.filters),
        ...forcedFilters
      }
    };

    const response = brokerScope
      ? await contractsAPI.getSummaryContracts({ getGenericListCommandDto: command })
      : await contractsAPI.getAdminContracts({ getGenericListCommandDto: command });
    const items: ContractListItem[] = response.items;

    return {
      items,
      totalItems: response.totalCount,
      totalPages: Math.ceil(response.totalCount / params.pageSize),
      currentPage: params.pageNumber
    };
  };

  const fetchContractFacets = (command: FacetQueryCommand) =>
    brokerScope
      ? contractsAPI.getContractListFacets({ getGenericListCommandDto: command })
      : contractsAPI.getAdminContractListFacets({ getGenericListCommandDto: command });

  // Utilitaires
  const formatDate = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number | undefined): string => {
    if (amount === undefined || amount === null) return 'N/A';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getStatusBadge = (statusLabel: string, statusColor: string) => {
    const colorClasses: Record<string, string> = {
      primary: 'bg-blue-100 text-blue-700 border-blue-300',
      secondary: 'bg-purple-100 text-purple-700 border-purple-300',
      green: 'bg-green-100 text-green-700 border-green-300',
      blue: 'bg-blue-100 text-blue-700 border-blue-300',
      orange: 'bg-orange-100 text-orange-700 border-orange-300',
      purple: 'bg-purple-100 text-purple-700 border-purple-300',
      yellow: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      pink: 'bg-pink-100 text-pink-700 border-pink-300',
      red: 'bg-red-100 text-red-700 border-red-300',
      gray: 'bg-gray-100 text-gray-700 border-gray-300',
    };

    const className = colorClasses[statusColor.toLowerCase()] || 'bg-gray-100 text-gray-700 border-gray-300';

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${className}`}>
        {statusLabel}
      </span>
    );
  };

  const tableContent = (
    <ServerTable<ContractListItem>
      // Configuration API
      apiConfig={{
        fetchData: fetchContracts,
      }}

      // Colonnes
      columns={[
        {
          key: 'contractIdentifier',
          sortKey: 'identifier',
          label: 'Identifiant',
          sortable: true,
          width: '180px',
          render: (value: any) => (
            <span className="font-mono font-semibold text-gray-900">
              {value?.label || 'N/A'}
            </span>
          )
        },
        {
          key: 'statusLabel',
          sortKey: 'status',
          label: 'Statut',
          sortable: true,
          width: '130px',
          render: (value: any, row: ContractListItem) => 
            getStatusBadge(String(value), row.statusColor)
        },
        {
          key: 'date',
          sortKey: 'date',
          label: 'Date',
          sortable: true,
          width: '120px',
          render: (value: any) => (
            <div className="flex items-center gap-2">
              <Calendar className="text-primary" size={16} />
              <span className="text-sm font-medium text-gray-900">
                {formatDate(String(value))}
              </span>
            </div>
          )
        },
        {
          key: 'insureds',
          sortKey: 'insured',
          label: 'Assurés',
          sortable: false,
          render: (value: any) => (
            <div className="flex items-center gap-2">
              <Users className="text-primary" size={16} />
              <span className="text-sm text-gray-700">
                {Array.isArray(value) && value.length > 0
                  ? value.map((insured: any) => insured.label).join(', ')
                  : 'N/A'}
              </span>
            </div>
          )
        },
        {
          key: 'branchCode',
          sortKey: 'branch',
          label: 'Branche',
          sortable: true,
          width: '100px',
          align: 'center',
          render: (value: any) => (
            <span className="px-2 py-1 bg-gray-100 rounded text-xs font-mono text-gray-700">
              {value}
            </span>
          )
        },
        {
          key: 'product',
          sortKey: 'productLabel',
          label: 'Produit',
          sortable: true,
          render: (value: any, row: ContractListItem) => (
            <div className="flex items-center gap-2">
              <Package className="text-primary" size={16} />
              <div>
                <div className="text-sm text-gray-700">{row.productLabel || value}</div>
                {row.productLabel && (
                  <div className="text-xs text-gray-400">{value}</div>
                )}
              </div>
            </div>
          )
        },
        {
          key: 'tunnelLabel',
          sortKey: 'tunnel',
          label: 'Tunnel',
          render: (value: any) => (
            <span className="text-sm text-gray-700">{value || '—'}</span>
          )
        },
        {
          key: 'effectiveDate',
          sortKey: 'effectiveDate',
          label: "Date d'effet",
          render: (value: any) => (
            <span className="text-sm text-gray-700">{value ? formatDate(value) : '—'}</span>
          )
        },
        {
          key: 'endDate',
          sortKey: 'endDate',
          label: 'Fin de contrat',
          render: (value: any) => (
            <span className="text-sm text-gray-700">{value ? formatDate(value) : '—'}</span>
          )
        },
        {
          key: 'mainDueDate',
          sortKey: 'mainDueDate',
          label: 'Échéance principale',
          render: (value: any) => (
            <span className="text-sm text-gray-700">{value || '—'}</span>
          )
        },
        {
          key: 'owner',
          label: 'Propriétaire',
          sortable: true,
          render: (value: any) => (
            <div className="flex items-center gap-2">
              <Shield className="text-primary" size={16} />
              <span className="text-sm text-gray-700">
                {value}
              </span>
            </div>
          )
        },
        {
          key: 'clientPrice',
          sortKey: 'clientPrice',
          label: 'Prix client',
          sortable: true,
          width: '120px',
          align: 'right',
          render: (value: any) => (
            <div className="flex items-center justify-end gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {formatCurrency(Number(value))}
              </span>
            </div>
          )
        },
        {
          key: 'brokerName',
          sortKey: 'broker',
          label: 'Courtier',
          sortable: true,
          render: (value: any) => (
            <div className="flex items-center gap-2">
              <Building2 className="text-primary" size={16} />
              <span className="text-sm text-gray-700">
                {value}
              </span>
            </div>
          )
        },
        // Plateforme : enrichissement admin uniquement (absent de la liste périmétrée)
        ...(brokerScope ? [] : [{
          key: 'plateformeName' as const,
          sortKey: 'plateforme',
          label: 'Plateforme',
          sortable: true,
          render: (value: any) => (
            <div className="flex items-center gap-2">
              <Network className="text-primary" size={16} />
              <span className="text-sm text-gray-700">
                {value}
              </span>
            </div>
          )
        }])
      ]}

      // En-tête
      title={title ? {
        label: title,
        iconKey: 'file-text'
      } : undefined}

      // Actions
      actions={{
        create: onCreateConfig,
        row: [
          {
            icon: <Eye size={16} />,
            onClick: handleView,
            variant: 'slate'
          },
        ],
      }}

      // Configuration
      selectable={selectable}
      serverSorting
      columnCustomization={{ storageKey: 'bo-contrats-groupe' }}
      pageSize={pageSize}
      pageSizeOptions={[5, 10, 20, 50]}
      showPaginationInfo

      // Recherche et filtres
      enableSearch
      enableFilters={enableFilters}
      facets={enableFilters ? { fetchFacets: fetchContractFacets } : undefined}
      // TODO: appeler la future route back d'export CSV (mêmes filtres que la liste)
      // Export CSV : route admin uniquement — désactivé en périmètre courtier.
      onExport={brokerScope ? undefined : (p) =>
        exportListCsv(
          '/api/backoffice/contrats/admin/liste/csv',
          buildExportCommand(p, { ...mapFacetFiltersToFiltersDto(p.filters), ...forcedFilters })
        )
      }
      searchPlaceholder="Rechercher un contrat (identifiant, propriétaire, produit, courtier...)"

      // Skeleton
      skeletonRowCount={8}
    />
  );

  return tableContent;
}
